import { useEffect, useMemo, useState } from "react";

import { listUsers } from "@/modules/users";
import type { User } from "@/modules/users";
import type { AuditFilterOption } from "./audit.types";

/**
 * O select de Responsável é populado pelo cadastro de usuários, e não pelos
 * registros já auditados: o auditor escolhe qualquer usuário do Hub, mesmo os
 * que não aparecem na página em tela. O serviço de usuários limita `pageSize` a
 * 100, então a base é percorrida página a página até completar o cadastro.
 */
export const AUDIT_RESPONSAVEL_PAGE_SIZE = 100;

/**
 * Teto de páginas percorridas (10 000 usuários). Protege a tela de um serviço
 * que devolva sempre uma página cheia e nunca sinalize o fim da base.
 */
export const AUDIT_RESPONSAVEL_MAX_PAGES = 100;

export interface UseAuditResponsavelOptionsResult {
  options: AuditFilterOption[];
  isLoading: boolean;
  hasError: boolean;
}

const EMPTY_OPTIONS: AuditFilterOption[] = [];

const COLLATOR = new Intl.Collator("pt-BR", { sensitivity: "base" });

/** Sem nome no diretório, o e-mail identifica o usuário no select. */
function toOption(user: User): AuditFilterOption | null {
  const label = user.nome.trim() || user.email.trim();

  return label ? { value: label, label } : null;
}

/**
 * Percorre o cadastro em páginas do tamanho máximo aceito pelo serviço e devolve
 * todos os usuários. Para na primeira página incompleta, ao alcançar o total
 * informado pelo serviço ou no teto de páginas — mesmo varrimento do select de
 * perfis (`useRoleOptions`).
 */
async function fetchAllUsers(signal: AbortSignal): Promise<User[]> {
  const collected: User[] = [];

  for (let page = 1; page <= AUDIT_RESPONSAVEL_MAX_PAGES; page += 1) {
    const { users, total } = await listUsers({
      page,
      pageSize: AUDIT_RESPONSAVEL_PAGE_SIZE,
      signal,
    });

    collected.push(...users);

    if (users.length < AUDIT_RESPONSAVEL_PAGE_SIZE || collected.length >= total) break;
  }

  return collected;
}

export function useAuditResponsavelOptions(): UseAuditResponsavelOptionsResult {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    fetchAllUsers(controller.signal)
      .then((allUsers) => {
        if (cancelled) return;
        setUsers(allUsers);
        setHasError(false);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setUsers([]);
        setHasError(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const options = useMemo(() => {
    if (users.length === 0) return EMPTY_OPTIONS;

    return users
      .map(toOption)
      .filter((option): option is AuditFilterOption => option !== null)
      .sort((first, second) => COLLATOR.compare(first.label, second.label));
  }, [users]);

  return { options, isLoading, hasError };
}
